import React from 'react';
import {
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Download,
  Error as ErrorIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import DownloadBulkUploadResultsButton from '../../Components/DownloadBulkUploadResultsButton/index.jsx';

const BulkUploadPanelValidationSection = ({
  hasValidationErrors = false,
  totalValidationErrorCount = 0,
  validationErrorRowCount = 0,
  validationErrorCategories = [],
  expandedErrorCategories = {},
  onToggleErrorCategory = () => undefined,
  isValidating = false,
  selectedFile = null,
  buildErrorDownloadContent = async () => '',
  hasValidationResult = false,
  validationSummary = null,
}) => (
  <>
    {hasValidationErrors && (
      <Box sx={{ marginTop: '1rem' }}>
        <Box
          sx={{
            border: '1px solid #FBD9D8',
            borderRadius: '8px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <ErrorIcon sx={{ color: '#CF2E2E', fontSize: '1rem', marginTop: '0.25rem' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <Typography sx={{ fontSize: '1rem', lineHeight: '1.5rem', fontWeight: 700, color: '#CF2E2E' }}>
                {`${totalValidationErrorCount} validation ${totalValidationErrorCount === 1 ? 'error' : 'errors'} in ${validationErrorRowCount} ${validationErrorRowCount === 1 ? 'row' : 'rows'}`}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 400, color: '#212121' }}>
                Fix them and re-upload.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {validationErrorCategories.map((category) => {
              const isExpanded = expandedErrorCategories[category.key] ?? !category.collapsible;
              return (
                <Box
                  key={category.key}
                  sx={{
                    backgroundColor: '#FAFAFA',
                    border: '1px solid #DEDEDE',
                    borderRadius: '8px',
                    padding: '0.75rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <Typography sx={{ fontSize: '0.75rem', lineHeight: '1.125rem', fontWeight: 500, color: '#212121' }}>
                      {`${category.label} (${category.count} ${category.count === 1 ? 'error' : 'errors'})`}
                    </Typography>
                    {category.collapsible && (
                      <IconButton
                        aria-label={`Toggle ${category.label} details`}
                        size="small"
                        onClick={() => onToggleErrorCategory(category.key)}
                        sx={{ color: '#797979', padding: 0 }}
                      >
                        {isExpanded ? (
                          <KeyboardArrowUp sx={{ fontSize: '1rem' }} />
                        ) : (
                          <KeyboardArrowDown sx={{ fontSize: '1rem' }} />
                        )}
                      </IconButton>
                    )}
                  </Box>
                  {isExpanded && category.preview.length > 0 && (
                    <>
                      <Box sx={{ width: '100%', height: '1px', backgroundColor: '#DEDEDE', marginY: '0.5rem' }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {category.preview.map((entry, index) => (
                          <Box
                            key={`${category.key}-${entry.lineNumber || 'line'}-${entry.value || entry.message}-${index}`}
                            sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}
                          >
                            {entry.lineNumber && (
                              <Typography sx={{ fontSize: '0.75rem', lineHeight: '1rem', fontWeight: 400, color: '#515151' }}>
                                {`Row ${entry.lineNumber}:`}
                              </Typography>
                            )}
                            {entry.value && (
                              <Box sx={{ backgroundColor: 'rgba(251, 217, 216, 0.25)', borderRadius: '4px', px: '0.375rem', py: '1px' }}>
                                <Typography sx={{ fontFamily: 'Menlo', fontSize: '0.75rem', lineHeight: '1rem', fontWeight: 400, color: '#CF2E2E' }}>
                                  {entry.value}
                                </Typography>
                              </Box>
                            )}
                            <Typography sx={{ fontSize: '0.75rem', lineHeight: '1rem', fontWeight: 400, color: '#515151' }}>
                              {entry.message}
                            </Typography>
                          </Box>
                        ))}
                        {category.count > category.preview.length && (
                          <Typography sx={{ fontSize: '0.6875rem', lineHeight: '1rem', fontWeight: 400, color: '#797979' }}>
                            {`+ ${category.count - category.preview.length} more errors in this category`}
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>

          <DownloadBulkUploadResultsButton
            label="Download error log"
            fileSuffix="errors"
            contentType="text/csv;charset=utf-8;"
            disabled={isValidating || !selectedFile}
            getContent={buildErrorDownloadContent}
            onError={(message) => {
              console.error(message);
            }}
            buttonProps={{
              variant: 'text',
              size: 'medium',
              startIcon: null,
              endIcon: <Download sx={{ fontSize: '1rem' }} />,
              sx: {
                alignSelf: 'flex-start',
                minWidth: 0,
                padding: 0,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                fontWeight: 500,
                color: 'primary.main',
                border: 'none',
                ':hover': {
                  border: 'none',
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              },
            }}
          />
        </Box>
      </Box>
    )}

    {hasValidationResult && validationSummary && (
      <Box sx={{ marginTop: '1rem' }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'chipPositive.contrastText' }}>
          {validationSummary}
        </Typography>
      </Box>
    )}
  </>
);

export default BulkUploadPanelValidationSection;
